/**
 * Runtime validation of a Character, for data that arrives from outside the type system: an
 * import file, an IndexedDB record written by an older build. Errors name the path that failed.
 */
import type { AnswerValue, Character, CharacterSpellcasting, Choice, ChoiceSource, Coins, Familiar, Inventory, InventoryItem, ItemMaterial, ItemRunes, KnownSpell, Level, ManualIWR, ManualSpellcastingEntry, MigrationRemoved, MigrationReopened, Override, ProgressionSlot, Reopened, Selection, SnapshotMigration, SpellcastingState, ToggleState, TraitToggles, VariantRules } from "./model";
import { CARRY_TYPES, CASTING_TYPES, CHARACTER_SCHEMA, MAGIC_TRADITIONS, isLevelNumber } from "./model";
import { PLAY_ACTION_KINDS } from "../session/actions";
import type { LogEntry, RollLog } from "../session/log";
import { validateSession } from "../session/validate";

export class ValidationError extends Error {
  override readonly name = "ValidationError";
  constructor(readonly path: string, message: string) {
    super(`${path}: ${message}`);
  }
}

type Obj = Record<string, unknown>;

function isObj(v: unknown): v is Obj {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function str(o: Obj, key: string, path: string, allowEmpty = false): string {
  const v = o[key];
  if (typeof v !== "string") throw new ValidationError(`${path}.${key}`, `expected a string, got ${describe(v)}`);
  if (!allowEmpty && v.length === 0) throw new ValidationError(`${path}.${key}`, "must not be empty");
  return v;
}

function bool(o: Obj, key: string, path: string): boolean {
  const v = o[key];
  if (typeof v !== "boolean") throw new ValidationError(`${path}.${key}`, `expected a boolean, got ${describe(v)}`);
  return v;
}

function num(o: Obj, key: string, path: string): number {
  const v = o[key];
  if (typeof v !== "number" || !Number.isFinite(v)) throw new ValidationError(`${path}.${key}`, `expected a number, got ${describe(v)}`);
  return v;
}

function arr(o: Obj, key: string, path: string): unknown[] {
  const v = o[key];
  if (!Array.isArray(v)) throw new ValidationError(`${path}.${key}`, `expected an array, got ${describe(v)}`);
  return v;
}

function describe(v: unknown): string {
  if (v === null) return "null";
  if (Array.isArray(v)) return "an array";
  return typeof v === "undefined" ? "nothing" : typeof v === "object" ? "an object" : `${typeof v} ${JSON.stringify(v)}`;
}

const ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})$/;

function toggles(v: unknown, path: string): Record<string, ToggleState> {
  if (v === undefined) return {};
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const out: Record<string, ToggleState> = {};
  for (const [key, value] of Object.entries(v)) {
    if (typeof value === "boolean") {
      out[key] = { checked: value };
      continue;
    }
    if (!isObj(value)) throw new ValidationError(`${path}.${key}`, `expected a boolean or an object, got ${describe(value)}`);
    const state: ToggleState = {};
    if (value.checked !== undefined) {
      if (typeof value.checked !== "boolean") throw new ValidationError(`${path}.${key}.checked`, `expected a boolean, got ${describe(value.checked)}`);
      state.checked = value.checked;
    }
    if (value.selection !== undefined) {
      if (typeof value.selection !== "string") throw new ValidationError(`${path}.${key}.selection`, `expected a string, got ${describe(value.selection)}`);
      state.selection = value.selection;
    }
    out[key] = state;
  }
  return out;
}

function timestamp(o: Obj, key: string, path: string): string {
  const v = str(o, key, path);
  if (!ISO.test(v)) throw new ValidationError(`${path}.${key}`, `expected an ISO timestamp, got ${JSON.stringify(v)}`);
  return v;
}

function selection(v: unknown, path: string): Selection {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  return { uuid: str(v, "uuid", path), snapshotTag: str(v, "snapshotTag", path) };
}

const PROGRESSION_SLOTS = new Set([
  "ancestry-feat", "class-feat", "class-feature", "general-feat", "skill-feat", "skill-increase",
  "attribute-boosts", "archetype-feat", "ancestry-paragon-feat", "dual-class-feat", "skill-potency", "attribute-apex", "familiar-ability", "apparition",
]);

function source(v: unknown, path: string): ChoiceSource {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const kind = str(v, "kind", path);
  switch (kind) {
    case "ancestry":
    case "heritage":
    case "background":
    case "class":
    case "dual-class":
      return { kind };
    case "progression": {
      const slot = str(v, "slot", path);
      if (!PROGRESSION_SLOTS.has(slot)) throw new ValidationError(`${path}.slot`, `unknown progression slot ${JSON.stringify(slot)}`);
      const ordinal = num(v, "ordinal", path);
      if (!Number.isInteger(ordinal) || ordinal < 0) throw new ValidationError(`${path}.ordinal`, "expected a non-negative integer");
      return { kind, slot: slot as ProgressionSlot, ordinal };
    }
    case "rule-element": {
      const index = num(v, "index", path);
      if (!Number.isInteger(index) || index < 0) throw new ValidationError(`${path}.index`, "expected a non-negative integer");
      return { kind, uuid: str(v, "uuid", path), index };
    }
    case "feature": {
      const id = str(v, "id", path);
      if (id.includes(":")) throw new ValidationError(`${path}.id`, "must not contain a colon");
      return { kind, uuid: str(v, "uuid", path), id };
    }
    default:
      throw new ValidationError(`${path}.kind`, `unknown Choice source ${JSON.stringify(kind)}`);
  }
}

function choice(v: unknown, path: string): Choice {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const out: Choice = { source: source(v.source, `${path}.source`) };
  if (v.selection !== undefined) out.selection = selection(v.selection, `${path}.selection`);
  if (v.answers !== undefined) {
    if (!isObj(v.answers)) throw new ValidationError(`${path}.answers`, `expected an object, got ${describe(v.answers)}`);
    const answers: Record<string, AnswerValue> = {};
    for (const [k, a] of Object.entries(v.answers)) answers[k] = answerValue(a, `${path}.answers.${k}`);
    out.answers = answers;
  }
  if (v.detached !== undefined) {
    if (v.detached !== true) throw new ValidationError(`${path}.detached`, `expected true or nothing, got ${describe(v.detached)}`);
    if (out.source.kind !== "rule-element" && out.source.kind !== "feature") throw new ValidationError(`${path}.detached`, "only a granted Choice can be detached");
    out.detached = true;
  }
  if (v.reopened !== undefined) {
    const list = arr(v, "reopened", path).map((r, i) => reopened(r, `${path}.reopened[${i}]`));
    if (list.length > 0) out.reopened = list;
  }
  return out;
}

function nullableStr(o: Obj, key: string, path: string): string | null {
  const v = o[key];
  if (v === null || v === undefined) return null;
  if (typeof v !== "string") throw new ValidationError(`${path}.${key}`, `expected a string or null, got ${describe(v)}`);
  return v;
}

function reopened(v: unknown, path: string): Reopened {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const reason = str(v, "reason", path);
  if (reason !== "missing" && reason !== "illegal") throw new ValidationError(`${path}.reason`, `expected "missing" or "illegal", got ${JSON.stringify(reason)}`);
  return { from: str(v, "from", path), to: str(v, "to", path), flag: nullableStr(v, "flag", path), uuid: nullableStr(v, "uuid", path), name: nullableStr(v, "name", path), reason, detail: nullableStr(v, "detail", path) };
}

const REMOVED_KINDS = new Set(["grant", "spell", "item", "condition", "effect", "consumable"]);

function migration(v: unknown, path: string): SnapshotMigration {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const redirected = num(v, "redirected", path);
  if (!Number.isInteger(redirected) || redirected < 0) throw new ValidationError(`${path}.redirected`, "expected a non-negative integer");
  const reopenedList: MigrationReopened[] = arr(v, "reopened", path).map((r, i) => {
    const p = `${path}.reopened[${i}]`;
    if (!isObj(r)) throw new ValidationError(p, `expected an object, got ${describe(r)}`);
    const lvl = num(r, "level", p);
    if (!isLevelNumber(lvl)) throw new ValidationError(`${p}.level`, `Level ${lvl} is outside 1 to 20`);
    return { ...reopened(r, p), key: str(r, "key", p), level: lvl, label: str(r, "label", p, true) };
  });
  const removed: MigrationRemoved[] = arr(v, "removed", path).map((r, i) => {
    const p = `${path}.removed[${i}]`;
    if (!isObj(r)) throw new ValidationError(p, `expected an object, got ${describe(r)}`);
    const kind = str(r, "kind", p);
    if (!REMOVED_KINDS.has(kind)) throw new ValidationError(`${p}.kind`, `unknown kind ${JSON.stringify(kind)}`);
    return { kind: kind as MigrationRemoved["kind"], uuid: str(r, "uuid", p), name: nullableStr(r, "name", p), detail: nullableStr(r, "detail", p) };
  });
  return { from: str(v, "from", path), to: str(v, "to", path), at: timestamp(v, "at", path), redirected, reopened: reopenedList, removed, seen: bool(v, "seen", path) };
}

function answerValue(a: unknown, path: string, depth = 0): AnswerValue {
  if (typeof a === "string" || typeof a === "number" || typeof a === "boolean") {
    if (typeof a === "number" && !Number.isFinite(a)) throw new ValidationError(path, "expected a finite number");
    return a;
  }
  if (isObj(a)) {
    if (depth >= 4) throw new ValidationError(path, "nested too deeply");
    const out: { [key: string]: AnswerValue } = {};
    for (const [k, v] of Object.entries(a)) out[k] = answerValue(v, `${path}.${k}`, depth + 1);
    return out;
  }
  throw new ValidationError(path, `expected a string, number, boolean or object, got ${describe(a)}`);
}

function level(v: unknown, path: string, expected: number): Level {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const number = num(v, "number", path);
  if (!isLevelNumber(number)) throw new ValidationError(`${path}.number`, `Level ${number} is outside 1 to 20`);
  if (number !== expected) throw new ValidationError(`${path}.number`, `expected Level ${expected} at this position, got ${number}`);
  return { number, choices: arr(v, "choices", path).map((c, i) => choice(c, `${path}.choices[${i}]`)) };
}

const IWR_KINDS = new Set(["immunity", "weakness", "resistance"]);

function strList(o: Obj, key: string, path: string): string[] {
  return arr(o, key, path).map((e, i) => {
    if (typeof e !== "string" || e.length === 0) throw new ValidationError(`${path}.${key}[${i}]`, `expected a type slug, got ${describe(e)}`);
    return e;
  });
}

function manualIWR(v: unknown, path: string): ManualIWR {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const kind = str(v, "kind", path);
  if (!IWR_KINDS.has(kind)) throw new ValidationError(`${path}.kind`, `unknown kind ${JSON.stringify(kind)}`);
  const out: ManualIWR = { kind: kind as ManualIWR["kind"], type: str(v, "type", path) };
  if (kind === "immunity") {
    if (v.value !== undefined) throw new ValidationError(`${path}.value`, "an immunity has no value");
  } else {
    const value = num(v, "value", path);
    if (!Number.isInteger(value) || value < 1) throw new ValidationError(`${path}.value`, `expected a whole number of 1 or more, got ${value}`);
    out.value = value;
  }
  if (v.exceptions !== undefined) out.exceptions = strList(v, "exceptions", path);
  if (v.doubleVs !== undefined) {
    if (kind !== "resistance") throw new ValidationError(`${path}.doubleVs`, "only a resistance doubles against types");
    out.doubleVs = strList(v, "doubleVs", path);
  }
  if (v.note !== undefined) out.note = str(v, "note", path, true);
  return out;
}

function override(v: unknown, path: string): Override {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const out: Override = { statistic: str(v, "statistic", path), value: num(v, "value", path) };
  if (v.note !== undefined) out.note = str(v, "note", path, true);
  return out;
}

const VARIANT_KEYS: (keyof VariantRules)[] = [
  "freeArchetype", "automaticBonusProgression", "gradualAttributeBoosts", "ancestryParagon", "dualClass", "proficiencyWithoutLevel",
];

function variantRules(v: unknown, path: string): VariantRules {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const out = {} as VariantRules;
  for (const k of VARIANT_KEYS) out[k] = bool(v, k, path);
  return out;
}

const RANK_KEY = /^(?:[0-9]|10)$/;
const ATTRIBUTES = new Set(["str", "dex", "con", "int", "wis", "cha"]);

function knownSpell(v: unknown, path: string): KnownSpell {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const out: KnownSpell = { uuid: str(v, "uuid", path) };
  if (v.rank !== undefined) {
    const rank = num(v, "rank", path);
    if (!Number.isInteger(rank) || rank < 0 || rank > 10) throw new ValidationError(`${path}.rank`, "expected a rank from 0 to 10");
    out.rank = rank;
  }
  if (v.signature !== undefined) {
    if (v.signature !== true) throw new ValidationError(`${path}.signature`, `expected true or nothing, got ${describe(v.signature)}`);
    out.signature = true;
  }
  if (v.choice !== undefined) out.choice = str(v, "choice", path);
  if (v.level !== undefined) {
    const level = num(v, "level", path);
    if (!isLevelNumber(level)) throw new ValidationError(`${path}.level`, `Level ${level} is outside 1 to 20`);
    out.level = level;
  }
  return out;
}

function spellcastingState(v: unknown, path: string): SpellcastingState {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const known = v.known === undefined ? [] : arr(v, "known", path).map((k, i) => knownSpell(k, `${path}.known[${i}]`));
  const prepared: Record<string, (string | null)[]> = {};
  if (v.prepared !== undefined) {
    if (!isObj(v.prepared)) throw new ValidationError(`${path}.prepared`, `expected an object, got ${describe(v.prepared)}`);
    for (const [rank, list] of Object.entries(v.prepared)) {
      if (!RANK_KEY.test(rank)) throw new ValidationError(`${path}.prepared.${rank}`, "expected a rank key from 0 to 10");
      if (!Array.isArray(list) || !list.every((u) => u === null || (typeof u === "string" && u.length > 0))) throw new ValidationError(`${path}.prepared.${rank}`, "expected spell UUIDs or nulls");
      prepared[rank] = [...(list as (string | null)[])];
    }
  }
  return { known, prepared };
}

function manualEntry(v: unknown, path: string): ManualSpellcastingEntry {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const id = str(v, "id", path);
  if (!/^manual:\d+$/.test(id)) throw new ValidationError(`${path}.id`, `expected "manual:<n>", got ${JSON.stringify(id)}`);
  const tradition = str(v, "tradition", path);
  if (!(MAGIC_TRADITIONS as readonly string[]).includes(tradition)) throw new ValidationError(`${path}.tradition`, `unknown tradition ${JSON.stringify(tradition)}`);
  const casting = str(v, "casting", path);
  if (!(CASTING_TYPES as readonly string[]).includes(casting)) throw new ValidationError(`${path}.casting`, `unknown casting type ${JSON.stringify(casting)}`);
  const attribute = str(v, "attribute", path);
  if (!ATTRIBUTES.has(attribute)) throw new ValidationError(`${path}.attribute`, `unknown attribute ${JSON.stringify(attribute)}`);
  const slots: Record<string, number> = {};
  if (v.slots !== undefined) {
    if (!isObj(v.slots)) throw new ValidationError(`${path}.slots`, `expected an object, got ${describe(v.slots)}`);
    for (const [rank, count] of Object.entries(v.slots)) {
      if (!RANK_KEY.test(rank)) throw new ValidationError(`${path}.slots.${rank}`, "expected a rank key from 0 to 10");
      if (typeof count !== "number" || !Number.isInteger(count) || count < 0) throw new ValidationError(`${path}.slots.${rank}`, "expected a non-negative integer");
      slots[rank] = count;
    }
  }
  return { id, name: str(v, "name", path), tradition: tradition as ManualSpellcastingEntry["tradition"], casting: casting as ManualSpellcastingEntry["casting"], attribute: attribute as ManualSpellcastingEntry["attribute"], slots };
}

function spellcasting(v: unknown, path: string): CharacterSpellcasting {
  if (v === undefined) return { entries: {}, manual: [] };
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const entries: Record<string, SpellcastingState> = {};
  if (v.entries !== undefined) {
    if (!isObj(v.entries)) throw new ValidationError(`${path}.entries`, `expected an object, got ${describe(v.entries)}`);
    for (const [id, state] of Object.entries(v.entries)) entries[id] = spellcastingState(state, `${path}.entries.${id}`);
  }
  const manual = v.manual === undefined ? [] : arr(v, "manual", path).map((m, i) => manualEntry(m, `${path}.manual[${i}]`));
  const ids = manual.map((m) => m.id);
  if (new Set(ids).size !== ids.length) throw new ValidationError(`${path}.manual`, "manual Entry ids must be unique");
  return { entries, manual };
}

const COIN_KEYS = ["pp", "gp", "sp", "cp"] as const;

function coins(v: unknown, path: string): Coins {
  if (v === undefined) return {};
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const out: Coins = {};
  for (const key of COIN_KEYS) {
    if (v[key] === undefined) continue;
    const n = num(v, key, path);
    if (!Number.isInteger(n) || n < 0) throw new ValidationError(`${path}.${key}`, "expected a non-negative integer");
    if (n > 0) out[key] = n;
  }
  return out;
}

function runes(v: unknown, path: string): ItemRunes {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const out: ItemRunes = {};
  for (const key of ["potency", "striking", "resilient", "reinforcing"] as const) {
    if (v[key] === undefined) continue;
    const n = num(v, key, path);
    if (!Number.isInteger(n) || n < 0 || n > (key === "reinforcing" ? 6 : 4)) throw new ValidationError(`${path}.${key}`, `expected an integer from 0 to ${key === "reinforcing" ? 6 : 4}`);
    out[key] = n;
  }
  if (v.property !== undefined) {
    const list = arr(v, "property", path);
    if (!list.every((p) => typeof p === "string" && p.length > 0)) throw new ValidationError(`${path}.property`, "expected rune slugs");
    out.property = [...(list as string[])];
  }
  return out;
}

function material(v: unknown, path: string): ItemMaterial {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const grade = str(v, "grade", path);
  if (grade !== "low" && grade !== "standard" && grade !== "high") throw new ValidationError(`${path}.grade`, `expected low, standard or high, got ${JSON.stringify(grade)}`);
  return { type: str(v, "type", path), grade };
}

function traitToggles(v: unknown, path: string): TraitToggles {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const out: TraitToggles = {};
  if (v.versatile !== undefined) {
    if (v.versatile !== null && typeof v.versatile !== "string") throw new ValidationError(`${path}.versatile`, `expected a string or null, got ${describe(v.versatile)}`);
    out.versatile = v.versatile;
  }
  if (v.modular !== undefined) {
    if (v.modular !== null && (typeof v.modular !== "number" || !Number.isInteger(v.modular))) throw new ValidationError(`${path}.modular`, `expected an integer or null, got ${describe(v.modular)}`);
    out.modular = v.modular;
  }
  if (v.doubleBarrel !== undefined) out.doubleBarrel = bool(v, "doubleBarrel", path);
  return out;
}

function inventoryItem(v: unknown, path: string): InventoryItem {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const id = str(v, "id", path);
  if (!/^i\d+$/.test(id)) throw new ValidationError(`${path}.id`, `expected "i<n>", got ${JSON.stringify(id)}`);
  const quantity = num(v, "quantity", path);
  if (!Number.isInteger(quantity) || quantity < 0) throw new ValidationError(`${path}.quantity`, "expected a non-negative integer");
  const carryType = str(v, "carryType", path);
  if (!(CARRY_TYPES as readonly string[]).includes(carryType)) throw new ValidationError(`${path}.carryType`, `unknown carry type ${JSON.stringify(carryType)}`);
  const handsHeld = num(v, "handsHeld", path);
  if (handsHeld !== 0 && handsHeld !== 1 && handsHeld !== 2) throw new ValidationError(`${path}.handsHeld`, "expected 0, 1 or 2");
  const out: InventoryItem = { id, uuid: str(v, "uuid", path), quantity, carryType: carryType as InventoryItem["carryType"], handsHeld };
  if (v.inSlot !== undefined) out.inSlot = bool(v, "inSlot", path);
  if (v.invested !== undefined) out.invested = bool(v, "invested", path);
  if (v.container !== undefined) out.container = str(v, "container", path);
  if (v.runes !== undefined) out.runes = runes(v.runes, `${path}.runes`);
  if (v.material !== undefined) out.material = material(v.material, `${path}.material`);
  if (v.size !== undefined) out.size = str(v, "size", path);
  if (v.hp !== undefined) {
    const hp = num(v, "hp", path);
    if (!Number.isInteger(hp) || hp < 0) throw new ValidationError(`${path}.hp`, "expected a non-negative integer");
    out.hp = hp;
  }
  if (v.ammo !== undefined) out.ammo = str(v, "ammo", path);
  if (v.traitToggles !== undefined) out.traitToggles = traitToggles(v.traitToggles, `${path}.traitToggles`);
  return out;
}

function inventory(v: unknown, path: string): Inventory {
  if (v === undefined) return { items: [], coins: {}, counter: 1 };
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const items = arr(v, "items", path).map((i, n) => inventoryItem(i, `${path}.items[${n}]`));
  const ids = new Set<string>();
  for (const item of items) {
    if (ids.has(item.id)) throw new ValidationError(`${path}.items`, `duplicate item id ${item.id}`);
    ids.add(item.id);
  }
  for (const item of items) {
    if (item.container !== undefined && !ids.has(item.container)) throw new ValidationError(`${path}.items`, `item ${item.id} is in a container that does not exist (${item.container})`);
    if (item.ammo !== undefined && !ids.has(item.ammo)) throw new ValidationError(`${path}.items`, `item ${item.id} selects ammunition that does not exist (${item.ammo})`);
  }
  const counter = v.counter === undefined ? 1 : num(v, "counter", path);
  if (!Number.isInteger(counter) || counter < 1) throw new ValidationError(`${path}.counter`, "expected a positive integer");
  return { items, coins: coins(v.coins, `${path}.coins`), counter };
}

function familiar(v: unknown, path: string): Familiar | null {
  if (v === null || v === undefined) return null;
  if (!isObj(v)) throw new ValidationError(path, `expected an object or null, got ${describe(v)}`);
  const attribute = v.attribute === null || v.attribute === undefined ? null : str(v, "attribute", path);
  if (attribute !== null && !ATTRIBUTES.has(attribute)) throw new ValidationError(`${path}.attribute`, `unknown attribute ${JSON.stringify(attribute)}`);
  return {
    name: str(v, "name", path),
    attribute: attribute as Familiar["attribute"],
    overrides: v.overrides === undefined ? [] : arr(v, "overrides", path).map((o, i) => override(o, `${path}.overrides[${i}]`)),
  };
}

const LOG_TYPES = new Set(["roll", "session", "note"]);

/** A stored Play Action: an object with a known kind. Its fields are checked when it is applied (an unknown id blocks the action). */
function playAction(v: unknown, path: string): LogEntry["action"] {
  if (v === null || v === undefined) return null;
  if (!isObj(v)) throw new ValidationError(path, `expected an object or null, got ${describe(v)}`);
  const kind = str(v, "kind", path);
  if (!PLAY_ACTION_KINDS.has(kind)) throw new ValidationError(`${path}.kind`, `unknown Play Action kind ${JSON.stringify(kind)}`);
  return structuredClone(v) as unknown as LogEntry["action"];
}

function logEntry(v: unknown, path: string): LogEntry {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const type = str(v, "type", path);
  if (!LOG_TYPES.has(type)) throw new ValidationError(`${path}.type`, `unknown entry type ${JSON.stringify(type)}`);
  const round = v.round === null || v.round === undefined ? null : num(v, "round", path);
  if (round !== null && (!Number.isInteger(round) || round < 1)) throw new ValidationError(`${path}.round`, "expected a positive integer or null");
  const events = arr(v, "events", path).map((e, i) => {
    if (!isObj(e)) throw new ValidationError(`${path}.events[${i}]`, `expected an object, got ${describe(e)}`);
    return { kind: str(e, "kind", `${path}.events[${i}]`) as LogEntry["events"][number]["kind"], text: typeof e.text === "string" ? e.text : "" };
  });
  const optional = (key: string): string | null => (v[key] === null || v[key] === undefined ? null : str(v, key, path));
  return {
    id: str(v, "id", path),
    at: timestamp(v, "at", path),
    round,
    kind: str(v, "kind", path),
    type: type as LogEntry["type"],
    summary: typeof v.summary === "string" ? v.summary : "",
    events,
    action: playAction(v.action, `${path}.action`),
    inverse: playAction(v.inverse, `${path}.inverse`),
    reversedBy: optional("reversedBy"),
    reverses: optional("reverses"),
  };
}

function rollLog(v: unknown, path: string, sessionOpen: boolean): RollLog {
  if (v === undefined) return { entries: [], dropped: 0, counter: 1 };
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const entries = arr(v, "entries", path).map((e, i) => logEntry(e, `${path}.entries[${i}]`));
  const ids = new Set<string>();
  for (const entry of entries) {
    if (ids.has(entry.id)) throw new ValidationError(`${path}.entries`, `duplicate entry id ${entry.id}`);
    ids.add(entry.id);
  }
  const dropped = v.dropped === undefined ? 0 : num(v, "dropped", path);
  if (!Number.isInteger(dropped) || dropped < 0) throw new ValidationError(`${path}.dropped`, "expected a non-negative integer");
  const counter = v.counter === undefined ? entries.length + dropped + 1 : num(v, "counter", path);
  if (!Number.isInteger(counter) || counter < 1) throw new ValidationError(`${path}.counter`, "expected a positive integer");
  if (!sessionOpen && entries.length > 0) throw new ValidationError(`${path}.entries`, "a Roll Log needs an open Session");
  return { entries, dropped, counter };
}

/**
 * Validate an unknown value as a Character of the current schema. Returns a fresh object holding
 * only known fields, so a validated Character never carries stray data forward. Throws
 * ValidationError naming the offending path.
 */
export function validateCharacter(v: unknown, path = "character"): Character {
  if (!isObj(v)) throw new ValidationError(path, `expected an object, got ${describe(v)}`);
  const schema = num(v, "schema", path);
  if (schema !== CHARACTER_SCHEMA) {
    throw new ValidationError(`${path}.schema`, `schema ${schema} is not ${CHARACTER_SCHEMA}; run migrateCharacter first`);
  }
  const lvl = num(v, "level", path);
  if (!isLevelNumber(lvl)) throw new ValidationError(`${path}.level`, `Level ${lvl} is outside 1 to 20`);
  const levels = arr(v, "levels", path).map((l, i) => level(l, `${path}.levels[${i}]`, i + 1));
  if (levels.length === 0) throw new ValidationError(`${path}.levels`, "must hold at least Level 1");
  if (lvl > levels.length) throw new ValidationError(`${path}.level`, `Level ${lvl} has not been opened (levels holds 1 to ${levels.length})`);
  const session: Character["session"] = v.session === null || v.session === undefined ? null : validateSession(v.session, `${path}.session`);
  return {
    schema: CHARACTER_SCHEMA,
    id: str(v, "id", path),
    name: str(v, "name", path),
    createdAt: timestamp(v, "createdAt", path),
    updatedAt: timestamp(v, "updatedAt", path),
    snapshotTag: str(v, "snapshotTag", path),
    level: lvl,
    levels,
    variantRules: variantRules(v.variantRules, `${path}.variantRules`),
    overrides: arr(v, "overrides", path).map((o, i) => override(o, `${path}.overrides[${i}]`)),
    iwr: arr(v, "iwr", path).map((o, i) => manualIWR(o, `${path}.iwr[${i}]`)),
    toggles: toggles(v.toggles, `${path}.toggles`),
    notes: typeof v.notes === "string" ? v.notes : "",
    spellcasting: spellcasting(v.spellcasting, `${path}.spellcasting`),
    inventory: inventory(v.inventory, `${path}.inventory`),
    session,
    rollLog: rollLog(v.rollLog, `${path}.rollLog`, session !== null),
    familiar: familiar(v.familiar, `${path}.familiar`),
    migrations: v.migrations === undefined ? [] : arr(v, "migrations", path).map((m, i) => migration(m, `${path}.migrations[${i}]`)),
  };
}
